import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { Integration } from '../types';
import { IntegrationFeature, IntegrationProvider } from '../types';
import { ZendeskConfigModal } from './ZendeskConfigModal';
import { ZendeskSubdomainModal, type ZendeskConnectOptions } from './ZendeskSubdomainModal';
import { IntegrationIcon } from '../icons';
import { toast } from 'sonner';
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle';
/**
 * Zendesk integration provider
 */
export const ZendeskProvider: IntegrationProvider = {
  id: 'Zendesk',
  name: 'Zendesk',
  description: 'Read Zendesk support tickets and staff access; manage Help Center connections',
  Icon: () => <IntegrationIcon type='Zendesk' />,
  ConfigModal: props => <ZendeskConfigModal {...props} onReconnect={ZendeskProvider.handleConnect} />,
  postAuthAction: 'CONFIGURE',

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'articles',
      name: 'Articles',
      description: 'Publish articles to Zendesk Guide',
      enabled: false,
    },
    {
      id: 'categories',
      name: 'Categories',
      description: 'Organize articles in categories',
      enabled: false,
    },
    {
      id: 'sections',
      name: 'Sections',
      description: 'Group articles in sections',
      enabled: false,
    },
    {
      id: 'tickets',
      name: 'Ticket Ingestion',
      description: 'Import support tickets for knowledge gap analysis',
      enabled: true,
    },
  ],

  // Handler to connect to Zendesk
  handleConnect: async ({ features, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish);
    try {
      const { createRoot } = await import('react-dom/client');
      const selection = await new Promise<ZendeskConnectOptions | null>((resolve) => {
        const modal = document.createElement('div');
        document.body.appendChild(modal);
        const root = createRoot(modal);
        let settled = false;
        const close = (value: ZendeskConnectOptions | null) => {
          if (settled) return;
          settled = true;
          root.unmount();
          modal.remove();
          resolve(value);
        };
        root.render(
          <ZendeskSubdomainModal
            open
            allowPublishing={features === undefined || features.some(feature => ['articles', 'categories', 'sections'].includes(feature))}
            onOpenChange={(open) => {
              if (!open) close(null);
            }}
            onSubmit={close}
          />
        );
      });
      if (!selection) {
        finish('closed');
        return;
      }
      const data = await trpcClient.integrations.initZendeskAuth.mutate(selection);

      if (!data.authUrl) {
        toast.error('Zendesk authorization could not start. Try again or ask your administrator to check the Zendesk app setup.');
        finish('error');
        return;
      }

      // Open auth page in a popup window
      openOAuthConnectPopup({
        authUrl: data.authUrl, windowName: 'zendesk-auth', provider: 'Zendesk',
        onSuccess: result => {
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getZendeskConnectionStatus.queryKey() });
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.listZendeskCategories.queryKey() });
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getZendeskTickets.queryKey() });
          onSuccess?.(result);
        },
        onError: () => toast.error('Zendesk authorization did not complete. Reconnect and approve the requested access.'),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: finish,
      });
    } catch {
      toast.error('Zendesk authorization could not start. Check your subdomain and ask your administrator to verify the Zendesk app setup.');
      finish('error');
    }
  },

  onConnectComplete: async ({ code, state }: { code: string, state: string }) => {
    const result = await trpcClient.integrations.completeZendeskAuth.mutate({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getZendeskConnectionStatus.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.listZendeskCategories.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getZendeskTickets.queryKey() }),
    ]);
    return result;
  },

  // Sync data with Zendesk
  syncData: async (integration: Integration) => {
    await queryClient.getMutationCache().build(queryClient, trpc.integrations.syncZendeskTickets.mutationOptions()).execute({ integrationId: integration.id });
    await queryClient.invalidateQueries({ queryKey: trpc.integrations.getZendeskConnectionStatus.queryKey() });
  },
};
